// Stillhouse view — Sessions (live + history). Live is wired to /api/fleet.
import React, { useEffect, useState } from "react";
import { C, F, Dot, Btn } from "../brand/apo";
import { Card, Chip, Avatar, Tabs, PageHeader, EmptyState } from "../brand/sh";
import { api, type FleetSession } from "../api";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);

function fmtUptime(sec: number) {
  const m = Math.floor(sec / 60);
  const h = Math.floor(m / 60);
  return `${String(h).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export function Sessions() {
  const [tab, setTab] = useState("live");
  const [live, setLive] = useState<FleetSession[] | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .getSessions()
      .then((r) => alive && setLive(r.live))
      .catch(() => alive && setLive([]));
    return () => {
      alive = false;
    };
  }, []);

  const list = live ?? [];
  return (
    <React.Fragment>
      <PageHeader
        crumb="FLEET › SESSIONS"
        title="Sessions"
        sub="Watch live streams and review the full connection history. Force-disconnect, flip privacy mode, or audit any session in one click."
      >
        <Btn ghost>Export log</Btn>
      </PageHeader>
      <div style={{ padding: "0 32px", borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: "live", label: "Live now", count: list.length },
            { id: "history", label: "History", count: 0 },
          ]}
        />
      </div>
      <Scroll>
        {tab === "live" ? (
          <Card pad={false}>
            <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1.3fr 1.2fr 1fr 0.7fr 0.7fr 1.4fr", gap: 12, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.14em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
              <span>MACHINE</span>
              <span>OPERATOR</span>
              <span>DEVICE</span>
              <span>RESOLUTION</span>
              <span>RTT</span>
              <span>UPTIME</span>
              <span>CONTROLS</span>
            </div>
            {list.length === 0 && <EmptyState title="No live sessions" sub="ACTIVE STREAMS APPEAR HERE" />}
            {list.map((s, i) => (
              <div key={s.id} style={{ display: "grid", gridTemplateColumns: "1.1fr 1.3fr 1.2fr 1fr 0.7fr 0.7fr 1.4fr", gap: 12, padding: "13px 18px", borderBottom: i < list.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "center", fontFamily: F.ui, fontSize: 12.5 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Dot color={C.glow} size={6} />
                  <span style={{ fontFamily: F.mono, fontWeight: 700, fontSize: 12 }}>{s.machine}</span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar name={s.operator} size={24} />
                  {s.operator}
                </span>
                <span style={{ color: C.fgMute }}>{s.device ?? "—"}</span>
                <span>{s.resolution ?? "—"}</span>
                <span style={{ fontFamily: F.mono, color: C.glow, fontWeight: 700 }}>{s.rttMs != null ? `${s.rttMs} ms` : "—"}</span>
                <span style={{ fontFamily: F.mono, color: C.fgMute }}>{fmtUptime(s.durationSec)}</span>
                <span style={{ display: "flex", gap: 7 }}>
                  {s.privacy && <Chip color={C.lilac}>Privacy</Chip>}
                  <Btn ghost size="md" style={{ padding: "4px 10px", fontSize: 11 }}>
                    Watch
                  </Btn>
                  <Btn size="md" style={{ padding: "4px 10px", fontSize: 11, background: `${C.rose}1c`, borderColor: `${C.rose}55`, color: C.rose }}>
                    Disconnect
                  </Btn>
                </span>
              </div>
            ))}
          </Card>
        ) : (
          <Card pad={false}>
            <EmptyState title="No session history yet" sub="COMPLETED SESSIONS APPEAR HERE" />
          </Card>
        )}
      </Scroll>
    </React.Fragment>
  );
}
